import React, { Suspense } from 'react';
import ReactDOM from 'react-dom/client';

// Ensure React is available globally to prevent issues with dependency loading
if (typeof window !== 'undefined') {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (window as any).React = React;
}

import ErrorBoundaryProvider from '@/components/providers/ErrorBoundaryProvider';
import { ThemeProvider } from '@/components/providers/ThemeProvider';
import { AccessDenied } from '@components/auth/AccessDenied';
import { AuthProvider } from '@components/auth/AuthContext';
import { CapabilitiesProvider } from '@components/auth/CapabilitiesContext';
import { ProtectedRoute } from '@components/auth/ProtectedRoute';
import { WithCapability } from '@components/auth/WithCapability';
import { AppLayout } from '@components/layout/AppLayout';
import { OfferProvider } from '@components/offer/OfferContext';
import { WhatsNewProvider } from '@components/whatsnew/WhatsNewContext';
import { initPreloading } from '@lib/preload';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { Alert, AlertDescription, AlertTitle } from '@ui/alert';
import { Button } from '@ui/button';
import { AuthenticatedRouteLoading, PublicRouteLoading, RouteLoading } from '@ui/route-loading';
import { Toaster } from '@ui/toaster';
import { Home, RefreshCcw } from 'lucide-react';
import {
  createBrowserRouter,
  isRouteErrorResponse,
  RouterProvider,
  useRouteError,
} from 'react-router-dom';

// Self-hosted Precision Editorial typefaces (DESIGN.md): Fraunces (display),
// Geist (UI/body), Geist Mono (money/data). Loaded once here so the whole SPA
// picks them up via the Tailwind `sans`/`serif`/`mono` families.
import '@fontsource-variable/fraunces';
// True italic face WITH the optical-size (opsz) axis. The sealer bakes a
// STATIC Fraunces 24pt-optical Italic instance (opsz=24, see
// document_sealing_service.SIGNATURE_FONT_*), so the preview must pin `opsz` 24
// via `.signature-preview-face`. The wght-only subset (wght-italic.css) carries
// no opsz axis, leaving the preview at the default ~9-14 optical size — a
// different glyph geometry than the sealed face. Without any italic face the
// browser also synthesizes an oblique, breaking parity outright.
import '@fontsource-variable/fraunces/opsz-italic.css';
import '@fontsource/geist/300.css';
import '@fontsource/geist/400.css';
import '@fontsource/geist/500.css';
import '@fontsource/geist/600.css';
import '@fontsource/geist-mono/400.css';
import '@fontsource/geist-mono/500.css';
import './index.css';

// LAZY LOADED ROUTE COMPONENTS

// Authentication & Public Pages
const LoginPage = React.lazy(() =>
  import('@components/auth/LoginPage').then(module => ({ default: module.LoginPage }))
);
const SignupPage = React.lazy(() =>
  import('@components/auth/SignupPage').then(module => ({ default: module.SignupPage }))
);
const EmailVerificationPage = React.lazy(() =>
  import('@components/auth/EmailVerificationPage').then(module => ({
    default: module.EmailVerificationPage,
  }))
);
const PasswordResetPage = React.lazy(() =>
  import('@components/auth/PasswordResetPage').then(module => ({
    default: module.PasswordResetPage,
  }))
);
const NoActiveCompanyPage = React.lazy(() =>
  import('@components/auth/NoActiveCompanyPage').then(module => ({
    default: module.NoActiveCompanyPage,
  }))
);
const CompanyOnboardingPage = React.lazy(() =>
  import('@components/auth/CompanyOnboardingPage').then(module => ({
    default: module.CompanyOnboardingPage,
  }))
);
const InvitationAcceptPage = React.lazy(() =>
  import('@components/auth/InvitationAcceptPage').then(module => ({
    default: module.InvitationAcceptPage,
  }))
);
const VerificationStatusPage = React.lazy(() =>
  import('@components/auth/VerificationStatusPage').then(module => ({
    default: module.VerificationStatusPage,
  }))
);

// Core App Pages
const DashboardPage = React.lazy(() =>
  import('@components/auth/DashboardPage').then(module => ({ default: module.DashboardPage }))
);

// Offer Management
const OfferForm = React.lazy(() => import('@components/offer/OfferForm'));
const ConsolidatedOfferView = React.lazy(() => import('@components/offer/ConsolidatedOfferView'));
const SignPage = React.lazy(() => import('@components/offer/SignPage'));
const AllOffersPage = React.lazy(() =>
  import('@components/offer/AllOffersPage').then(module => ({ default: module.AllOffersPage }))
);

// Company Management
const CompanyBrandingPage = React.lazy(() => import('@components/company/CompanyBrandingPage'));
const CompanySettingsPage = React.lazy(() => import('@components/company/CompanySettingsPage'));
const OfferSettingsPage = React.lazy(() => import('@components/company/OfferSettingsPage'));
const ValuationAndComparablesPage = React.lazy(
  () => import('@components/company/ValuationAndComparablesPage')
);
const ManageUsersPage = React.lazy(() =>
  import('@components/company/ManageUsersPage').then(module => ({
    default: module.ManageUsersPage,
  }))
);
const BenefitTemplatesPage = React.lazy(() =>
  import('@components/company/BenefitTemplatesPage').then(module => ({
    default: module.BenefitTemplatesPage,
  }))
);
const ResolveDocumentFieldsPage = React.lazy(
  () => import('@components/offer/ResolveDocumentFieldsPage')
);
const DocumentTemplateDetailPage = React.lazy(
  () => import('@components/company/DocumentTemplateDetailPage')
);
const DocumentTemplatesPage = React.lazy(() =>
  import('@components/company/DocumentTemplatesPage').then(module => ({
    default: module.DocumentTemplatesPage,
  }))
);
const SalaryEquityBandsPage = React.lazy(() => import('@components/company/SalaryEquityBandsPage'));
const InternalEquityPage = React.lazy(() => import('@components/company/InternalEquityPage'));

const IntegrationsPage = React.lazy(() => import('@components/company/IntegrationsPage'));
const SSOConfigurationPage = React.lazy(() =>
  import('@components/company/SSOConfigurationPage').then(module => ({
    default: module.SSOConfigurationPage,
  }))
);
const BillingPageWrapper = React.lazy(() => import('@components/billing/BillingPageWrapper'));
const AuditLogPage = React.lazy(() =>
  import('@components/company/AuditLogPage').then(module => ({
    default: module.AuditLogPage,
  }))
);

// User Settings
const UserSettingsPage = React.lazy(() => import('@/components/settings/UserSettingsPage'));

// Public Demo Page
const DemoPage = React.lazy(() => import('@/components/demo/DemoPage'));
const GeneratedOfferPage = React.lazy(() => import('@/components/tools/GeneratedOfferPage'));

// SUSPENSE WRAPPER COMPONENTS

// Wrapper for public pages with appropriate loading UI
const PublicPageWrapper: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Suspense fallback={<PublicRouteLoading />}>{children}</Suspense>
);

// Wrapper for authenticated pages with appropriate loading UI
const AuthenticatedPageWrapper: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Suspense fallback={<AuthenticatedRouteLoading />}>{children}</Suspense>
);

// Wrapper for general pages with basic loading UI
const PageWrapper: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Suspense fallback={<RouteLoading />}>{children}</Suspense>
);

// ERROR BOUNDARY

// Create a custom route error element to handle React Router errors
const RouteErrorBoundary = () => {
  const error = useRouteError();
  let errorMessage = 'An unexpected error occurred';
  let statusCode = 'Error';

  if (isRouteErrorResponse(error)) {
    statusCode = error.status.toString();
    errorMessage = error.statusText || error.data?.message || 'Page not found';
  } else if (error instanceof Error) {
    errorMessage = error.message;
  }

  return (
    <div className="min-h-screen flex flex-col items-center justify-center p-6 bg-muted">
      <div className="w-full max-w-md">
        <Alert variant="destructive" className="mb-6 border-2 border-destructive/20">
          <AlertTitle className="text-xl font-bold mb-2">{statusCode} Error</AlertTitle>
          <AlertDescription className="mt-2">
            <p className="text-muted-foreground mb-4">{errorMessage}</p>
            <div className="flex flex-col sm:flex-row gap-3 mt-4">
              <Button onClick={() => window.location.reload()}>
                <RefreshCcw className="h-4 w-4 mr-2" />
                Try Again
              </Button>
              <Button variant="outline" onClick={() => (window.location.href = '/')}>
                <Home className="h-4 w-4 mr-2" />
                Return to Home
              </Button>
            </div>
          </AlertDescription>
        </Alert>
      </div>
    </div>
  );
};

// REACT QUERY CONFIGURATION

// Create a client for React Query
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes
      retry: 1,
    },
  },
});

// ROUTER CONFIGURATION

// Define our routes with lazy loading
const router = createBrowserRouter([
  {
    path: '/',
    element: <AppLayout />,
    errorElement: <RouteErrorBoundary />,
    children: [
      {
        path: 'demo',
        element: (
          <PublicPageWrapper>
            <DemoPage />
          </PublicPageWrapper>
        ),
        errorElement: <RouteErrorBoundary />,
      },
      {
        // Public shareable link for a free generated offer (JOB-184, no auth).
        path: 'o/:token',
        element: (
          <PublicPageWrapper>
            <GeneratedOfferPage />
          </PublicPageWrapper>
        ),
        errorElement: <RouteErrorBoundary />,
      },
      {
        path: 'login',
        element: (
          <PublicPageWrapper>
            <LoginPage />
          </PublicPageWrapper>
        ),
        errorElement: <RouteErrorBoundary />,
      },
      {
        path: 'no-active-company',
        element: (
          <PublicPageWrapper>
            <NoActiveCompanyPage />
          </PublicPageWrapper>
        ),
        errorElement: <RouteErrorBoundary />,
      },
      {
        path: 'complete-signup',
        element: (
          <PublicPageWrapper>
            <ProtectedRoute>
              <CompanyOnboardingPage />
            </ProtectedRoute>
          </PublicPageWrapper>
        ),
        errorElement: <RouteErrorBoundary />,
      },
      {
        path: 'signup',
        element: (
          <PublicPageWrapper>
            <SignupPage />
          </PublicPageWrapper>
        ),
        errorElement: <RouteErrorBoundary />,
      },
      {
        path: 'accounts/signup',
        element: (
          <PublicPageWrapper>
            <SignupPage />
          </PublicPageWrapper>
        ),
        errorElement: <RouteErrorBoundary />,
      },
      {
        path: 'email-verification',
        element: (
          <PublicPageWrapper>
            <EmailVerificationPage />
          </PublicPageWrapper>
        ),
        errorElement: <RouteErrorBoundary />,
      },
      {
        path: 'reset-password',
        element: (
          <PublicPageWrapper>
            <PasswordResetPage />
          </PublicPageWrapper>
        ),
        errorElement: <RouteErrorBoundary />,
      },
      {
        path: 'reset-password/confirm',
        element: (
          <PublicPageWrapper>
            <PasswordResetPage />
          </PublicPageWrapper>
        ),
        errorElement: <RouteErrorBoundary />,
      },
      {
        path: 'dashboard',
        element: (
          <ProtectedRoute>
            <AuthenticatedPageWrapper>
              <DashboardPage />
            </AuthenticatedPageWrapper>
          </ProtectedRoute>
        ),
        errorElement: <RouteErrorBoundary />,
      },
      {
        path: 'offers/create',
        element: (
          <ProtectedRoute>
            <AuthenticatedPageWrapper>
              <OfferForm mode="create" />
            </AuthenticatedPageWrapper>
          </ProtectedRoute>
        ),
        errorElement: <RouteErrorBoundary />,
      },
      {
        path: 'offers/:offerId',
        element: (
          <ProtectedRoute>
            <AuthenticatedPageWrapper>
              <ConsolidatedOfferView />
            </AuthenticatedPageWrapper>
          </ProtectedRoute>
        ),
        errorElement: <RouteErrorBoundary />,
      },
      {
        // The merge-field resolver. A dedicated route, not a drawer: it is a
        // two-pane workspace with a document beside the decisions, and it has
        // to be linkable — the send-block signpost and the offer editor's
        // return path both navigate straight back to a specific card.
        path: 'offers/:offerId/documents/resolve',
        element: (
          <ProtectedRoute>
            <AuthenticatedPageWrapper>
              <WithCapability capability="offers.update" fallback={<AccessDenied />}>
                <ResolveDocumentFieldsPage />
              </WithCapability>
            </AuthenticatedPageWrapper>
          </ProtectedRoute>
        ),
        errorElement: <RouteErrorBoundary />,
      },
      {
        path: 'offers/edit/:offerId',
        element: (
          <ProtectedRoute>
            <AuthenticatedPageWrapper>
              <OfferForm mode="edit" />
            </AuthenticatedPageWrapper>
          </ProtectedRoute>
        ),
        errorElement: <RouteErrorBoundary />,
      },
      {
        path: 'offers/all',
        element: (
          <ProtectedRoute>
            <AuthenticatedPageWrapper>
              <AllOffersPage />
            </AuthenticatedPageWrapper>
          </ProtectedRoute>
        ),
        errorElement: <RouteErrorBoundary />,
      },
      {
        path: 'company',
        element: (
          <ProtectedRoute>
            <AuthenticatedPageWrapper>
              <CompanySettingsPage />
            </AuthenticatedPageWrapper>
          </ProtectedRoute>
        ),
        errorElement: <RouteErrorBoundary />,
      },
      {
        path: 'company/settings',
        element: (
          <ProtectedRoute>
            <AuthenticatedPageWrapper>
              <CompanySettingsPage />
            </AuthenticatedPageWrapper>
          </ProtectedRoute>
        ),
        errorElement: <RouteErrorBoundary />,
      },
      {
        path: 'company/branding',
        element: (
          <ProtectedRoute>
            <AuthenticatedPageWrapper>
              <CompanyBrandingPage />
            </AuthenticatedPageWrapper>
          </ProtectedRoute>
        ),
        errorElement: <RouteErrorBoundary />,
      },
      {
        path: 'company/offer-settings',
        element: (
          <ProtectedRoute>
            <AuthenticatedPageWrapper>
              <OfferSettingsPage />
            </AuthenticatedPageWrapper>
          </ProtectedRoute>
        ),
        errorElement: <RouteErrorBoundary />,
      },
      {
        path: 'company/valuation-comparables',
        element: (
          <ProtectedRoute>
            <AuthenticatedPageWrapper>
              <WithCapability capability="valuations.view" fallback={<AccessDenied />}>
                <ValuationAndComparablesPage />
              </WithCapability>
            </AuthenticatedPageWrapper>
          </ProtectedRoute>
        ),
        errorElement: <RouteErrorBoundary />,
      },
      {
        path: 'company/users',
        element: (
          <ProtectedRoute>
            <AuthenticatedPageWrapper>
              <WithCapability
                capability="company_settings.manage_users"
                fallback={<AccessDenied />}
              >
                <ManageUsersPage />
              </WithCapability>
            </AuthenticatedPageWrapper>
          </ProtectedRoute>
        ),
        errorElement: <RouteErrorBoundary />,
      },
      {
        path: 'company/benefit-templates',
        element: (
          <ProtectedRoute>
            <AuthenticatedPageWrapper>
              <BenefitTemplatesPage />
            </AuthenticatedPageWrapper>
          </ProtectedRoute>
        ),
        errorElement: <RouteErrorBoundary />,
      },
      {
        path: 'company/document-templates',
        element: (
          <ProtectedRoute>
            <AuthenticatedPageWrapper>
              <DocumentTemplatesPage />
            </AuthenticatedPageWrapper>
          </ProtectedRoute>
        ),
        errorElement: <RouteErrorBoundary />,
      },
      {
        // Template detail = the resolver at template scope. Reads need
        // `document_templates.view`; the endpoint enforces `.manage` on write,
        // so a viewer can see the decisions without being able to change them.
        path: 'company/document-templates/:templateId',
        element: (
          <ProtectedRoute>
            <AuthenticatedPageWrapper>
              <WithCapability capability="document_templates.view" fallback={<AccessDenied />}>
                <DocumentTemplateDetailPage />
              </WithCapability>
            </AuthenticatedPageWrapper>
          </ProtectedRoute>
        ),
        errorElement: <RouteErrorBoundary />,
      },
      {
        path: 'company/salary-equity-bands',
        element: (
          <ProtectedRoute>
            <AuthenticatedPageWrapper>
              <SalaryEquityBandsPage />
            </AuthenticatedPageWrapper>
          </ProtectedRoute>
        ),
        errorElement: <RouteErrorBoundary />,
      },
      {
        path: 'company/internal-equity',
        element: (
          <ProtectedRoute>
            <AuthenticatedPageWrapper>
              <InternalEquityPage />
            </AuthenticatedPageWrapper>
          </ProtectedRoute>
        ),
        errorElement: <RouteErrorBoundary />,
      },
      {
        path: 'company/integrations',
        element: (
          <ProtectedRoute>
            <AuthenticatedPageWrapper>
              <WithCapability capability="integrations.view" fallback={<AccessDenied />}>
                <IntegrationsPage />
              </WithCapability>
            </AuthenticatedPageWrapper>
          </ProtectedRoute>
        ),
        errorElement: <RouteErrorBoundary />,
      },
      {
        path: 'company/sso',
        element: (
          <ProtectedRoute>
            <AuthenticatedPageWrapper>
              <WithCapability capability="company_settings.manage_sso" fallback={<AccessDenied />}>
                <SSOConfigurationPage />
              </WithCapability>
            </AuthenticatedPageWrapper>
          </ProtectedRoute>
        ),
        errorElement: <RouteErrorBoundary />,
      },
      {
        path: 'company/billing',
        element: (
          <ProtectedRoute>
            <AuthenticatedPageWrapper>
              <WithCapability capability="billing.manage" fallback={<AccessDenied />}>
                <BillingPageWrapper />
              </WithCapability>
            </AuthenticatedPageWrapper>
          </ProtectedRoute>
        ),
        errorElement: <RouteErrorBoundary />,
      },
      {
        path: 'company/audit-log',
        element: (
          <ProtectedRoute>
            <AuthenticatedPageWrapper>
              <WithCapability capability="audit.view" fallback={<AccessDenied />}>
                <AuditLogPage />
              </WithCapability>
            </AuthenticatedPageWrapper>
          </ProtectedRoute>
        ),
        errorElement: <RouteErrorBoundary />,
      },
      {
        path: 'user-settings',
        element: (
          <ProtectedRoute>
            <AuthenticatedPageWrapper>
              <UserSettingsPage />
            </AuthenticatedPageWrapper>
          </ProtectedRoute>
        ),
        errorElement: <RouteErrorBoundary />,
      },
      {
        path: 'email-verification-status',
        element: (
          <PublicPageWrapper>
            <VerificationStatusPage />
          </PublicPageWrapper>
        ),
        errorElement: <RouteErrorBoundary />,
      },
      {
        path: 'invite/accept',
        element: (
          <PublicPageWrapper>
            <InvitationAcceptPage />
          </PublicPageWrapper>
        ),
        errorElement: <RouteErrorBoundary />,
      },
      {
        path: 'offers/view/:signedData',
        element: (
          <PageWrapper>
            <ConsolidatedOfferView />
          </PageWrapper>
        ),
        errorElement: <RouteErrorBoundary />,
      },
      {
        // Document-scoped signing link (screenshare flow): documents only,
        // never offer content. Server route: core/urls.py sign/<signed_data>/
        // (keep SPA_ROUTE_PREFIXES in sync).
        path: 'sign/:signedData',
        element: (
          <PublicPageWrapper>
            <SignPage />
          </PublicPageWrapper>
        ),
        errorElement: <RouteErrorBoundary />,
      },
    ],
  },
]);

// APPLICATION BOOTSTRAP

document.addEventListener('DOMContentLoaded', () => {
  try {
    const rootElement = document.getElementById('root');

    if (rootElement) {
      ReactDOM.createRoot(rootElement).render(
        <React.StrictMode>
          <ErrorBoundaryProvider>
            <ThemeProvider>
              <QueryClientProvider client={queryClient}>
                <AuthProvider>
                  <CapabilitiesProvider>
                    <OfferProvider>
                      <WhatsNewProvider>
                        <RouterProvider router={router} />
                        <Toaster />
                      </WhatsNewProvider>
                    </OfferProvider>
                  </CapabilitiesProvider>
                </AuthProvider>
                <ReactQueryDevtools initialIsOpen={false} />
              </QueryClientProvider>
            </ThemeProvider>
          </ErrorBoundaryProvider>
        </React.StrictMode>
      );

      // Initialize preloading strategies after the app has loaded
      // Wait a bit to let the initial render complete
      setTimeout(() => {
        try {
          initPreloading();
        } catch (error) {
          console.error('Error initializing preloading:', error);
        }
      }, 1000);
    } else {
      console.error('Root element not found');
    }
  } catch (error) {
    console.error('Error initializing React app:', error);
    // Fallback: show a basic error message
    const rootElement = document.getElementById('root');
    if (rootElement) {
      rootElement.innerHTML = `
        <div style="padding: 20px; text-align: center; color: #141A1F; background: #FAF9F6; font-family: 'Geist', ui-sans-serif, system-ui, sans-serif;">
          <h1>Application Error</h1>
          <p style="color: #6B6862;">Failed to initialize the application. Please refresh the page.</p>
          <details style="margin-top: 10px; text-align: left; color: #6B6862;">
            <summary>Error Details</summary>
            <pre style="background: #F4F3EF; color: #141A1F; padding: 10px; border-radius: 4px; overflow: auto;">${error}</pre>
          </details>
        </div>
      `;
    }
  }
});
